import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import { tokens } from '../../../theme';

const packageIdsKey = (list) => list.map((p) => String(p.PackageID)).join(',');

function PackageRowContent({ pkg, index, colors, dragHandleProps, isDragging, isOverlay }) {
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: colors.primary[400],
        boxShadow: isOverlay ? 6 : isDragging ? 4 : 0,
        border: isOverlay ? `2px solid ${colors.greenAccent[500]}` : 'none',
        opacity: isDragging && !isOverlay ? 0.45 : 1,
      }}
    >
      <IconButton
        size="small"
        sx={{
          color: colors.grey[300],
          cursor: isOverlay ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
        aria-label="Drag to reorder"
        {...dragHandleProps}
      >
        <DragIndicatorIcon />
      </IconButton>
      <Typography
        sx={{
          minWidth: 28,
          fontWeight: 'bold',
          color: colors.greenAccent[400],
        }}
      >
        {index + 1}
      </Typography>
      <Box flex={1}>
        <Typography color={colors.grey[100]} fontWeight={600}>
          {pkg.PackageName}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          ID {pkg.PackageID}
        </Typography>
      </Box>
    </Paper>
  );
}

function SortablePackageRow({ pkg, index, colors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: pkg.PackageID,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <PackageRowContent
        pkg={pkg}
        index={index}
        colors={colors}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </Box>
  );
}

const PackageOrder = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [searchParams, setSearchParams] = useSearchParams();

  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState(searchParams.get('countryId') || '');
  const [packages, setPackages] = useState([]);
  const [savedOrderKey, setSavedOrderKey] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortableIds = useMemo(() => packages.map((p) => p.PackageID), [packages]);
  const isDirty = packages.length > 0 && packageIdsKey(packages) !== savedOrderKey;
  const activePackage = activeId != null ? packages.find((p) => p.PackageID === activeId) : null;
  const activeIndex = activePackage ? packages.indexOf(activePackage) : -1;

  const loadCountries = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } });
    const data = await res.json();
    if (Array.isArray(data)) {
      setCountries(data);
    }
  };

  const applyPackages = (list) => {
    setPackages(list);
    setSavedOrderKey(packageIdsKey(list));
  };

  const loadPackages = async (cid) => {
    if (!cid) {
      setPackages([]);
      setSavedOrderKey('');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrypackages/${cid}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.packages)) {
        applyPackages(data.packages);
      } else {
        setPackages([]);
        setSavedOrderKey('');
        setOpenToast(true);
        setToastMessage(data.message || 'Failed to load packages');
      }
    } catch (err) {
      setPackages([]);
      setSavedOrderKey('');
      setOpenToast(true);
      setToastMessage('Error loading packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    if (countryId) {
      loadPackages(countryId);
      setSearchParams({ countryId: String(countryId) }, { replace: true });
    } else {
      setPackages([]);
      setSavedOrderKey('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    setPackages((items) => {
      const oldIndex = items.findIndex((p) => p.PackageID === active.id);
      const newIndex = items.findIndex((p) => p.PackageID === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const saveOrder = async () => {
    if (!countryId || !packages.length) return;
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/countrypackages/${countryId}/order`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          token,
        },
        body: JSON.stringify({
          packageIds: packages.map((p) => p.PackageID),
        }),
      });
      const data = await res.json();
      setOpenToast(true);
      setToastMessage(data.message || (data.success ? 'Order saved' : 'Failed to save order'));
      if (data.success) {
        setSavedOrderKey(packageIdsKey(packages));
        await loadPackages(countryId);
      }
    } catch (err) {
      setOpenToast(true);
      setToastMessage('Error saving package order');
    } finally {
      setSaving(false);
    }
  };

  const selectedCountryName =
    countries.find((c) => String(c.CountryID) === String(countryId))?.CountryName || '';

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header
          title="PACKAGE DISPLAY ORDER"
          subtitle="Drag packages to set order on passenger app (top = first)"
        />
        <Link to="/package">
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Back to packages
          </Button>
        </Link>
      </Box>

      <Box mt={3} maxWidth={720}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="package-order-country-label" style={{ color: colors.grey[300] }}>
            Destination (country)
          </InputLabel>
          <Select
            labelId="package-order-country-label"
            label="Destination (country)"
            value={countryId}
            onChange={(e) => setCountryId(e.target.value)}
          >
            <MenuItem value="">
              <em>Select country</em>
            </MenuItem>
            {countries.map((c) => (
              <MenuItem key={c.CountryID} value={c.CountryID}>
                {c.CountryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {countryId ? (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
              <Typography variant="body2" color={colors.grey[300]}>
                {selectedCountryName}: drag rows by the handle, then save.
                {isDirty ? (
                  <Typography component="span" color={colors.greenAccent[400]} sx={{ ml: 1 }}>
                    Unsaved changes
                  </Typography>
                ) : null}
              </Typography>
              <Button
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  '&:hover': { backgroundColor: colors.greenAccent[700] },
                  '&.Mui-disabled': { opacity: 0.6 },
                }}
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={!packages.length || saving || !isDirty}
                onClick={saveOrder}
              >
                {saving ? 'Saving…' : 'Save display order'}
              </Button>
            </Box>

            {loading ? (
              <Typography color={colors.grey[300]}>Loading packages…</Typography>
            ) : packages.length === 0 ? (
              <Typography color={colors.grey[300]}>No packages for this destination.</Typography>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={({ active }) => setActiveId(active.id)}
                onDragEnd={handleDragEnd}
                onDragCancel={() => setActiveId(null)}
              >
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {packages.map((pkg, index) => (
                      <SortablePackageRow
                        key={pkg.PackageID}
                        pkg={pkg}
                        index={index}
                        colors={colors}
                      />
                    ))}
                  </Box>
                </SortableContext>
                <DragOverlay>
                  {activePackage ? (
                    <PackageRowContent
                      pkg={activePackage}
                      index={activeIndex}
                      colors={colors}
                      isOverlay
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>
            )}
          </>
        ) : null}
      </Box>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <IconButton size="small" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default PackageOrder;
